import { contrastReport, deriveColors, isHex, mergeTheme, nocturne, type ThemeVariant } from '@ce/theme';
import type { NavTarget } from '../types';
import {
  CONFIG_SCHEMA, type AppConfig, type ConfigIssue, type NavStyle, type PageDef,
  type TabDef, type TabTarget, type WidgetInstance,
} from './types';
import { NAV_SCREENS, WIDGETS, isWidgetType, type PropField, type WidgetDef } from './registry';
import { defaultAppConfig } from './defaults';

/**
 * Two jobs, and they are different jobs.
 *
 * `sanitizeConfig` runs in the *apps*, at load. Its contract is that it never
 * throws and always returns something renderable: a config the admin would have
 * refused, or one from a newer admin with widgets this build has never heard
 * of, degrades to a working app rather than a blank one.
 *
 * `validateConfig` runs in the *admin* and on the *server*, before publish. Its
 * contract is the opposite — say everything that is wrong, so the person fixing
 * it can. Repair silently at load; refuse loudly at the door.
 */

const NAV_SCREEN_SET = new Set(NAV_SCREENS.map((s) => s.value));
const NAV_STYLES: NavStyle[] = ['bottom', 'floating', 'top', 'drawer'];
const BUILTIN_TAB_SCREENS = new Set([
  'schedule', 'talent', 'hall', 'quests', 'me', 'events', 'search',
  'journey', 'shop', 'tickets', 'messages',
]);

/**
 * A bar and a menu have different limits. A row of tabs stops working past
 * seven; a drawer is a list and happily holds a dozen. Six is the recommended
 * ceiling for a bar because that is what the app has always shipped.
 */
export const MAX_TABS = 7;
export const MAX_DRAWER_ITEMS = 12;
export const RECOMMENDED_TABS = 6;

export function maxTabsFor(style: NavStyle): number {
  return style === 'drawer' ? MAX_DRAWER_ITEMS : MAX_TABS;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

// ── Field-level repair ──────────────────────────────────────────────────────

function emptyFor(f: PropField): unknown {
  switch (f.kind) {
    case 'text': case 'textarea': case 'datetime': case 'session': return '';
    case 'number': return f.min ?? 0;
    case 'toggle': return false;
    case 'select': return f.options?.[0]?.value ?? '';
    case 'nav': return null;
    case 'list': return [];
    case 'track': return 'All';
    case 'day': return 'selected';
  }
}

export function sanitizeNav(v: unknown): NavTarget | null {
  if (!isObj(v) || typeof v.screen !== 'string' || !NAV_SCREEN_SET.has(v.screen)) return null;
  const out: NavTarget = { screen: v.screen as NavTarget['screen'] };
  if (typeof v.track === 'string' && v.track) out.track = v.track.slice(0, 40);
  if (v.hallTab === 'map' || v.hallTab === 'exhibitors') out.hallTab = v.hallTab;
  if (typeof v.exZone === 'string' && v.exZone) out.exZone = v.exZone.slice(0, 40);
  if (typeof v.talentFilter === 'string' && v.talentFilter) out.talentFilter = v.talentFilter.slice(0, 40);
  return out;
}

function sanitizeField(f: PropField, v: unknown, fallback: unknown): unknown {
  switch (f.kind) {
    case 'text': case 'textarea':
      return typeof v === 'string' ? v.slice(0, f.maxLength ?? 400) : fallback;
    case 'session': case 'track': case 'day':
      return typeof v === 'string' ? v.slice(0, 60) : fallback;
    case 'number': {
      if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
      return Math.round(Math.min(f.max ?? Infinity, Math.max(f.min ?? -Infinity, v)));
    }
    case 'toggle':
      return typeof v === 'boolean' ? v : fallback;
    case 'select':
      return f.options?.some((o) => o.value === v) ? v : fallback;
    case 'datetime':
      return typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : fallback;
    case 'nav':
      return v === null ? null : sanitizeNav(v) ?? (fallback ?? null);
    case 'list': {
      if (!Array.isArray(v)) return fallback;
      const max = f.maxItems ?? 20;
      if (f.item === 'text') {
        return v
          .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
          .map((x) => x.slice(0, 120))
          .slice(0, max);
      }
      const fields = f.item ?? [];
      return v
        .filter(isObj)
        .map((row) => {
          const out: Record<string, unknown> = {};
          for (const sub of fields) out[sub.key] = sanitizeField(sub, row[sub.key], emptyFor(sub));
          return out;
        })
        // A row missing a required field is unusable, so drop it rather than
        // render an empty card.
        .filter((row) => fields.every((sub) => !sub.required || String(row[sub.key] ?? '').trim()))
        .slice(0, max);
    }
  }
}

export function sanitizeProps(def: WidgetDef, props: unknown): Record<string, unknown> {
  const src = isObj(props) ? props : {};
  const out: Record<string, unknown> = {};
  for (const f of def.fields) {
    const fallback = def.defaults[f.key] !== undefined ? def.defaults[f.key] : emptyFor(f);
    out[f.key] = sanitizeField(f, src[f.key], fallback);
  }

  // Quiz answers are 1-based in the editor; keep `correct` inside its own list.
  if (def.type === 'quizCard' && Array.isArray(out.questions)) {
    out.questions = (out.questions as { a: string[]; correct: number }[])
      .filter((q) => Array.isArray(q.a) && q.a.length >= 2)
      .map((q) => ({ ...q, correct: Math.min(Math.max(1, q.correct), q.a.length) }));
  }
  return out;
}

// ── Config-level repair ─────────────────────────────────────────────────────

export interface ResolvedConfig {
  config: AppConfig;
  /** Human-readable notes on anything that was dropped or repaired. */
  notes: string[];
}

function sanitizeWidget(raw: unknown, notes: string[]): WidgetInstance | null {
  if (!isObj(raw)) return null;
  if (!isWidgetType(raw.type)) {
    notes.push(`Dropped unknown widget type "${String(raw.type)}"`);
    return null;
  }
  const def = WIDGETS[raw.type];
  const id = typeof raw.id === 'string' && raw.id ? raw.id.slice(0, 60) : '';
  if (!id) {
    notes.push(`Dropped a ${raw.type} widget with no id`);
    return null;
  }
  const out: WidgetInstance = { id, type: raw.type, props: sanitizeProps(def, raw.props) };
  if (raw.hidden === true) out.hidden = true;
  if (raw.audience === 'badge' || raw.audience === 'nobadge') out.audience = raw.audience;
  if (Array.isArray(raw.days)) {
    const days = raw.days.filter((d): d is string => typeof d === 'string').slice(0, 10);
    if (days.length) out.days = days;
  }
  return out;
}

function sanitizePage(raw: unknown, key: string, notes: string[]): PageDef | null {
  if (!isObj(raw)) return null;
  const seen = new Set<string>();
  const widgets: WidgetInstance[] = [];
  for (const wr of Array.isArray(raw.widgets) ? raw.widgets : []) {
    const wi = sanitizeWidget(wr, notes);
    if (!wi) continue;
    // A duplicate id would make two widgets share one poll, or one dismissal.
    if (seen.has(wi.id)) {
      notes.push(`Dropped duplicate widget id "${wi.id}"`);
      continue;
    }
    seen.add(wi.id);
    widgets.push(wi);
  }
  return {
    id: key,
    title: typeof raw.title === 'string' && raw.title ? raw.title.slice(0, 40) : key,
    kicker: typeof raw.kicker === 'string' ? raw.kicker.slice(0, 60) : '',
    widgets: widgets.slice(0, 40),
  };
}

function sanitizeTarget(raw: unknown, pages: Record<string, PageDef>): TabTarget | null {
  if (!isObj(raw)) return null;
  if (raw.kind === 'page' && typeof raw.pageId === 'string' && pages[raw.pageId]) {
    return { kind: 'page', pageId: raw.pageId };
  }
  if (raw.kind === 'screen' && typeof raw.screen === 'string' && BUILTIN_TAB_SCREENS.has(raw.screen)) {
    return { kind: 'screen', screen: raw.screen as never };
  }
  return null;
}

/**
 * Turns whatever arrived into a config the apps can render.
 * Never throws. Unknown input yields the default.
 */
export function sanitizeConfig(raw: unknown, tenantId = 'nocturne-con'): ResolvedConfig {
  const fallback = defaultAppConfig(tenantId);
  const notes: string[] = [];

  if (!isObj(raw)) return { config: fallback, notes: ['No usable config; using the default layout'] };

  if (typeof raw.schema === 'number' && raw.schema > CONFIG_SCHEMA) {
    return {
      config: fallback,
      notes: [`Config schema ${raw.schema} is newer than this app understands (${CONFIG_SCHEMA}); using the default layout`],
    };
  }

  // Pages first — tabs are validated against them.
  const pages: Record<string, PageDef> = {};
  if (isObj(raw.pages)) {
    for (const [key, val] of Object.entries(raw.pages).slice(0, 12)) {
      if (!/^[a-z0-9-]{1,32}$/.test(key)) {
        notes.push(`Dropped page with invalid id "${key}"`);
        continue;
      }
      const page = sanitizePage(val, key, notes);
      if (page) pages[key] = page;
    }
  }
  if (!pages.home) {
    notes.push('No home page; restored the default');
    pages.home = fallback.pages.home;
  }

  // Navigation
  const nav = isObj(raw.navigation) ? raw.navigation : {};
  const style: NavStyle = NAV_STYLES.includes(nav.style as NavStyle) ? (nav.style as NavStyle) : 'bottom';

  const tabs: TabDef[] = [];
  const tabIds = new Set<string>();
  for (const tr of Array.isArray(nav.tabs) ? nav.tabs : []) {
    if (!isObj(tr)) continue;
    const target = sanitizeTarget(tr.target, pages);
    const id = typeof tr.id === 'string' && tr.id ? tr.id.slice(0, 40) : '';
    if (!target || !id || tabIds.has(id)) {
      notes.push(`Dropped a tab (${typeof tr.label === 'string' ? tr.label : 'unnamed'}) with a missing target or id`);
      continue;
    }
    tabIds.add(id);
    tabs.push({
      id,
      label: typeof tr.label === 'string' && tr.label.trim() ? tr.label.trim().slice(0, 20) : 'Tab',
      icon: typeof tr.icon === 'string' ? tr.icon.slice(0, 20) : 'today',
      target,
      enabled: tr.enabled !== false,
    });
  }
  let liveTabs = tabs.filter((t) => t.enabled);
  if (liveTabs.length === 0) {
    notes.push('No usable tabs; restored the default navigation');
    tabs.splice(0, tabs.length, ...fallback.navigation.tabs);
    liveTabs = tabs;
  }
  // Beyond the cap, the bar stops being a bar. Keep the first N enabled.
  const cap = maxTabsFor(style);
  if (liveTabs.length > cap) {
    notes.push(`More than ${cap} tabs; the extras were hidden`);
    const keep = new Set(liveTabs.slice(0, cap).map((t) => t.id));
    for (const t of tabs) if (t.enabled && !keep.has(t.id)) t.enabled = false;
  }

  const hdr = isObj(nav.header) ? nav.header : {};
  const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);

  const feat = isObj(raw.features) ? raw.features : {};
  const fx = isObj(raw.effects) ? raw.effects : {};

  // Variants: keep well-formed ones only.
  const variants: ThemeVariant[] = [];
  if (Array.isArray(raw.themeVariants)) {
    const seen = new Set<string>();
    for (const v of raw.themeVariants.slice(0, 12)) {
      if (!isObj(v) || typeof v.id !== 'string' || typeof v.label !== 'string' || seen.has(v.id)) continue;
      seen.add(v.id);
      variants.push({
        id: v.id.slice(0, 32),
        label: v.label.slice(0, 24),
        note: typeof v.note === 'string' ? v.note.slice(0, 80) : '',
        patch: isObj(v.patch) ? (v.patch as ThemeVariant['patch']) : {},
      });
    }
  } else {
    variants.push(...fallback.themeVariants);
  }

  const config: AppConfig = {
    schema: CONFIG_SCHEMA,
    tenantId: typeof raw.tenantId === 'string' ? raw.tenantId : tenantId,
    revision: typeof raw.revision === 'string' ? raw.revision : 'unversioned',
    theme: isObj(raw.theme) ? (raw.theme as AppConfig['theme']) : {},
    style: isObj(raw.style) ? (raw.style as unknown as AppConfig['style']) : undefined,
    themeVariants: variants,
    navigation: {
      style,
      showLabels: bool(nav.showLabels, true),
      tabs,
      header: {
        search: bool(hdr.search, true),
        inbox: bool(hdr.inbox, true),
        cart: bool(hdr.cart, true),
        grid: bool(hdr.grid, true),
      },
    },
    pages,
    features: {
      journey: bool(feat.journey, true),
      tickets: bool(feat.tickets, true),
      shop: bool(feat.shop, true),
      themePicker: bool(feat.themePicker, true),
      callSheet: bool(feat.callSheet, true),
      askAssistant: bool(feat.askAssistant, true),
    },
    effects: {
      ambient: bool(fx.ambient, true),
      entrance: bool(fx.entrance, true),
      rotatorMotion: bool(fx.rotatorMotion, true),
    },
  };

  return { config, notes };
}

// ── Validation ──────────────────────────────────────────────────────────────

export interface ValidationContext {
  days?: string[];
  tracks?: string[];
  sessionIds?: string[];
}

function fieldIssues(
  def: WidgetDef, inst: WidgetInstance, pageId: string, ctx: ValidationContext,
): ConfigIssue[] {
  const out: ConfigIssue[] = [];
  const base = { pageId, widgetId: inst.id, focus: 'widget' as const };
  const say = (severity: ConfigIssue['severity'], message: string) =>
    out.push({ severity, message: `${def.label}: ${message}`, ...base });

  const check = (f: PropField, v: unknown, path: string) => {
    const label = path || f.label;
    if (f.showWhen && (inst.props[f.showWhen.key] as unknown) !== f.showWhen.equals && !path.includes('.')) return;

    switch (f.kind) {
      case 'text': case 'textarea':
        if (f.required && !(typeof v === 'string' && v.trim())) say('error', `${label} is required`);
        else if (typeof v === 'string' && f.maxLength && v.length > f.maxLength) say('warning', `${label} is over ${f.maxLength} characters and will be cut`);
        break;
      case 'number':
        if (typeof v !== 'number' || !Number.isFinite(v)) say('error', `${label} must be a number`);
        else if ((f.min !== undefined && v < f.min) || (f.max !== undefined && v > f.max)) say('error', `${label} must be between ${f.min} and ${f.max}`);
        break;
      case 'datetime':
        if (typeof v !== 'string' || Number.isNaN(Date.parse(v))) say('error', `${label} needs a valid date and time`);
        break;
      case 'select':
        if (!f.options?.some((o) => o.value === v)) say('error', `${label} has an unsupported value`);
        break;
      case 'nav':
        if (v !== null && v !== undefined && !sanitizeNav(v)) say('error', `${label} points nowhere`);
        break;
      case 'track':
        if (ctx.tracks && typeof v === 'string' && v !== 'All' && !ctx.tracks.includes(v)) say('warning', `Track "${v}" is not in the content`);
        break;
      case 'day':
        if (ctx.days && typeof v === 'string' && v !== 'selected' && !ctx.days.includes(v)) say('warning', `Day "${v}" is not in the content`);
        break;
      case 'session':
        if (ctx.sessionIds && typeof v === 'string' && v && !ctx.sessionIds.includes(v)) say('warning', `Session "${v}" is not in the content`);
        break;
      case 'list': {
        const arr = Array.isArray(v) ? v : [];
        if (f.minItems !== undefined && arr.length < f.minItems) say('error', `${label} needs at least ${f.minItems}`);
        if (f.maxItems !== undefined && arr.length > f.maxItems) say('error', `${label} allows at most ${f.maxItems}`);
        if (f.item === 'text') {
          if (arr.some((x) => typeof x !== 'string' || !x.trim())) say('error', `${label} has an empty entry`);
        } else if (f.item) {
          arr.forEach((row, i) => {
            for (const sub of f.item as PropField[]) {
              check(sub, isObj(row) ? row[sub.key] : undefined, `${f.itemLabel ?? 'Item'} ${i + 1} \u2192 ${sub.label}`);
            }
          });
        }
        break;
      }
      default:
        break;
    }
  };

  for (const f of def.fields) check(f, inst.props[f.key], '');

  if (def.type === 'quizCard' && inst.props.source === 'custom' && Array.isArray(inst.props.questions)) {
    (inst.props.questions as { a?: unknown[]; correct?: number }[]).forEach((q, i) => {
      const n = Array.isArray(q.a) ? q.a.length : 0;
      if (typeof q.correct === 'number' && (q.correct < 1 || q.correct > n)) {
        say('error', `Question ${i + 1}: the correct answer points past the last option`);
      }
    });
  }
  return out;
}

/**
 * Everything wrong with a config, in one pass.
 *
 * Errors block publish. Warnings do not — they name things that will work but
 * probably are not what the author meant, like a widget for a feature that is
 * switched off.
 */
export function validateConfig(config: AppConfig, ctx: ValidationContext = {}): ConfigIssue[] {
  const issues: ConfigIssue[] = [];

  if (config.schema !== CONFIG_SCHEMA) {
    issues.push({ severity: 'error', message: `Schema ${config.schema} is not supported (expected ${CONFIG_SCHEMA})` });
  }

  // Pages and widgets
  if (!config.pages.home) {
    issues.push({ severity: 'error', message: 'There is no home page', focus: 'page' });
  }
  const seenIds = new Map<string, string>();

  for (const [key, page] of Object.entries(config.pages)) {
    if (page.id !== key) issues.push({ severity: 'error', message: `Page "${key}" has a mismatched id`, pageId: key, focus: 'page' });
    if (!page.title.trim()) issues.push({ severity: 'error', message: `Page "${key}" needs a title`, pageId: key, focus: 'page' });
    if (page.widgets.length === 0) issues.push({ severity: 'warning', message: `"${page.title}" has no widgets, so it will be blank`, pageId: key, focus: 'page' });

    for (const inst of page.widgets) {
      if (seenIds.has(inst.id)) {
        issues.push({ severity: 'error', message: `Widget id "${inst.id}" is used twice (${seenIds.get(inst.id)} and ${page.title})`, pageId: key, widgetId: inst.id, focus: 'widget' });
      }
      seenIds.set(inst.id, page.title);

      if (!isWidgetType(inst.type)) {
        issues.push({ severity: 'error', message: `Unknown widget type "${String(inst.type)}"`, pageId: key, widgetId: inst.id, focus: 'widget' });
        continue;
      }
      const def = WIDGETS[inst.type];
      issues.push(...fieldIssues(def, inst, key, ctx));

      if (def.requires && !config.features[def.requires] && !inst.hidden) {
        issues.push({
          severity: 'warning',
          message: `${def.label} is on "${page.title}" but the ${def.requires} feature is off, so it will not appear`,
          pageId: key, widgetId: inst.id, focus: 'widget',
        });
      }
    }
  }

  // Navigation
  const tabs = config.navigation.tabs;
  const enabled = tabs.filter((t) => t.enabled);
  if (enabled.length === 0) issues.push({ severity: 'error', message: 'At least one tab must be enabled', focus: 'navigation' });
  const cap = maxTabsFor(config.navigation.style);
  if (enabled.length > cap) {
    issues.push({ severity: 'error', message: `${enabled.length} items is too many for this navigation style; the maximum is ${cap}`, focus: 'navigation' });
  } else if (enabled.length > RECOMMENDED_TABS && config.navigation.style !== 'drawer') {
    issues.push({ severity: 'warning', message: `${enabled.length} tabs is cramped on a phone; ${RECOMMENDED_TABS} or fewer reads better (or use the drawer style)`, focus: 'navigation' });
  }
  const tabIds = new Set<string>();
  for (const t of tabs) {
    if (tabIds.has(t.id)) issues.push({ severity: 'error', message: `Tab id "${t.id}" is used twice`, focus: 'navigation' });
    tabIds.add(t.id);
    if (!t.label.trim()) issues.push({ severity: 'error', message: 'A tab has no label', focus: 'navigation' });
    else if (t.label.length > 12 && config.navigation.style !== 'drawer') issues.push({ severity: 'warning', message: `Tab "${t.label}" is long and will be squeezed`, focus: 'navigation' });
    if (t.target.kind === 'page' && !config.pages[t.target.pageId]) {
      issues.push({ severity: 'error', message: `Tab "${t.label}" points at a page that no longer exists`, focus: 'navigation' });
    }
  }
  const targets = enabled.map((t) => JSON.stringify(t.target));
  if (new Set(targets).size !== targets.length) {
    issues.push({ severity: 'warning', message: 'Two tabs open the same screen', focus: 'navigation' });
  }
  // A page nobody can reach is dead weight the author probably did not intend.
  for (const key of Object.keys(config.pages)) {
    if (key === 'home') continue;
    const linked = tabs.some((t) => t.enabled && t.target.kind === 'page' && t.target.pageId === key);
    if (!linked) issues.push({ severity: 'warning', message: `Page "${config.pages[key].title}" has no tab, so attendees can only reach it from a link`, pageId: key, focus: 'page' });
  }

  // Style
  const resolved = mergeTheme(nocturne, config.theme);
  const c = resolved.colors;
  for (const [k, v] of Object.entries({ bg: c.bg, surface: c.surface, text: c.text, accent: c.accent })) {
    if (!isHex(v)) issues.push({ severity: 'error', message: `Colour "${k}" is not a hex value`, focus: 'style' });
  }
  if ([c.bg, c.surface, c.text, c.accent].every(isHex)) {
    const r = contrastReport(c);
    if (r.text < 3) issues.push({ severity: 'error', message: `Text is nearly unreadable on the background (${r.text.toFixed(1)}:1, needs 4.5:1)`, focus: 'style' });
    else if (r.text < 4.5) issues.push({ severity: 'warning', message: `Text contrast is ${r.text.toFixed(1)}:1; 4.5:1 is the accessible minimum`, focus: 'style' });
    if (r.accentOnSurface < 3) issues.push({ severity: 'warning', message: `The accent is hard to see on cards (${r.accentOnSurface.toFixed(1)}:1)`, focus: 'style' });
  }

  const vIds = new Set<string>();
  for (const v of config.themeVariants) {
    if (vIds.has(v.id)) issues.push({ severity: 'error', message: `Theme variant id "${v.id}" is used twice`, focus: 'style' });
    vIds.add(v.id);
  }

  return issues;
}

export function hasErrors(issues: ConfigIssue[]): boolean {
  return issues.some((i) => i.severity === 'error');
}

/** Derives a full ThemePatch colour set — re-exported for the admin's style panel. */
export { deriveColors };
